import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { EReviewSubmissionStatus } from '@onesource/schemas';
import { getMediaUrl, useGetReviewSubmission } from '@onesource/services';
import { useNavigate } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { FC, ReactNode, useMemo, useState } from 'react';
import { MdArrowBack, MdBlock, MdOutlineMail, MdPersonAdd } from 'react-icons/md';

import { useContentRoute } from '../../hooks/use-content-route';
import { AnswerList } from './answer-list';
import { AssignTeacherDialog } from './assign-teacher-dialog';
import { getAnswerItems } from './feedback-answer-text';
import { FeedbackEditor } from './feedback-editor';
import { NotifySubmissionDialog } from './notify-submission-dialog';
import { RejectSubmissionDialog } from './reject-submission-dialog';
import { ReviewSplitView } from './review-split-view';
import { ReviewStatusChip } from './review-status-chip';
import { ReviewSubmissionSkeleton } from './review-submission-skeleton';
import { SubmissionNotes } from './submission-notes';
import { useAppHeaderHeight, useElementHeight } from './use-element-height';

const formatDate = (value: string | null | undefined) =>
  value ? DateTime.fromJSDate(new Date(value)).toLocaleString(DateTime.DATETIME_MED) : '';

// On a phone the header buttons show only their icon (the tooltip names them), so the
// pinned card stays one line.
const ICON_ON_PHONE_SX = {
  textTransform: 'none',
  minWidth: { xs: 36, md: 64 },
  px: { xs: 1, md: 1.5 },
  '& .MuiButton-startIcon': { mr: { xs: 0, md: 1 }, ml: { xs: 0, md: -0.5 } },
} as const;
const LABEL_FROM_MD_SX = { display: { xs: 'none', md: 'inline' } } as const;

/** A white, rounded section with a small heading — the page's one card style. */
const Section: FC<{ title: string; aside?: ReactNode; children: ReactNode }> = ({ title, aside, children }) => (
  // `clip`, not `hidden`: hidden makes the card a scroll box and breaks the sticky Send bar inside it.
  <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'clip', bgcolor: 'common.white' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: { xs: 2, md: 3 },
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>{title}</Typography>
      {aside}
    </Box>
    <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>{children}</Box>
  </Paper>
);

/**
 * A student's Hands-On Practice submission: their answers on the left, the
 * teacher's feedback in a panel that stays in view on the right. The assigned
 * teacher can also reject it, with a reason, so the student submits again.
 */
export const ReviewSubmissionPage: FC<{ submissionId: string }> = ({ submissionId }) => {
  const { programSlug, subSpaceId = '', subSpaceSlug = '' } = useContentRoute();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetReviewSubmission(subSpaceId, submissionId);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const appHeaderHeight = useAppHeaderHeight();
  const [stickyHeaderRef, stickyHeaderHeight] = useElementHeight();
  const answerItems = useMemo(() => (data ? getAnswerItems(data.form.questions, data.answers) : []), [data]);

  const backToQueue = () =>
    navigate({ to: '/programs/$programSlug/$moduleSlug', params: { programSlug, moduleSlug: subSpaceSlug } });

  const backButton = (
    <Button
      size="small"
      variant="text"
      startIcon={<MdArrowBack />}
      onClick={backToQueue}
      sx={{ mb: 1.5, ml: -1, textTransform: 'none', color: 'text.secondary', bgcolor: 'transparent' }}
    >
      All submissions
    </Button>
  );

  if (isLoading) {
    return <ReviewSubmissionSkeleton />;
  }
  if (error || !data) {
    return (
      <Box>
        {backButton}
        <Alert severity="error">{error?.message ?? 'Submission not found'}</Alert>
      </Box>
    );
  }

  const isDelivered = data.status === EReviewSubmissionStatus.reviewed;
  const isRejected = data.status === EReviewSubmissionStatus.rejected;
  const sentLine = data.feedback?.submittedAt
    ? `Sent ${formatDate(data.feedback.submittedAt)}${
        data.feedback.updatedAt && data.feedback.updatedAt !== data.feedback.submittedAt
          ? ` · updated ${formatDate(data.feedback.updatedAt)}`
          : ''
      }`
    : null;

  const feedbackTabLabel = isRejected
    ? 'Feedback'
    : isDelivered
      ? 'Feedback · Sent'
      : data.feedback
        ? 'Feedback · Draft'
        : 'Feedback';

  return (
    <Box sx={{ pb: 6 }}>
      {/*
        The way back and who this is stay in view while the teacher reads a long
        submission; the answers and feedback scroll under it.
      */}
      <Box
        ref={stickyHeaderRef}
        sx={{
          position: 'sticky',
          top: appHeaderHeight,
          zIndex: 3,
          // The page's own tone, so content scrolls under it cleanly.
          bgcolor: '#f4efee',
          pb: { xs: 1.5, md: 2 },
        }}
      >
        {backButton}

        {/* Who and what — one line of context, the status on the right. */}
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, md: 2 },
            px: { xs: 1.5, md: 3 },
            py: { xs: 1.25, md: 2 },
            borderRadius: '12px',
            bgcolor: 'common.white',
          }}
        >
          <Avatar
            src={data.student.avatar ? getMediaUrl(data.student.avatar) : undefined}
            sx={{ width: { xs: 36, md: 48 }, height: { xs: 36, md: 48 } }}
          >
            {data.student.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.3 }} noWrap>
              {data.student.name}
            </Typography>
            {/* One line on a phone, so the pinned card stays short. */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.25,
                whiteSpace: { xs: 'nowrap', md: 'normal' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {[data.form.title, data.moduleName].filter(Boolean).join(' · ')}
              {' · '}Submitted {formatDate(data.submittedAt)}
              {!data.canEdit && data.reviewer ? ` · Teacher: ${data.reviewer.name}` : ''}
              {!data.reviewer ? ' · No teacher assigned' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, flexShrink: 0 }}>
            <ReviewStatusChip status={data.status} />
            {data.canNotify && (
              <Tooltip title="Send a note about this submission to its teacher or its student.">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MdOutlineMail />}
                  onClick={() => setIsNotifyOpen(true)}
                  aria-label="Send a note"
                  sx={ICON_ON_PHONE_SX}
                >
                  <Box component="span" sx={LABEL_FROM_MD_SX}>
                    Send a note
                  </Box>
                </Button>
              </Tooltip>
            )}
            {data.canAssignTeacher && (
              <Tooltip title="Pick the teacher who reviews this student's Hands-On Practice in this program.">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MdPersonAdd />}
                  onClick={() => setIsAssignOpen(true)}
                  aria-label="Assign teacher"
                  sx={ICON_ON_PHONE_SX}
                >
                  <Box component="span" sx={LABEL_FROM_MD_SX}>
                    Assign teacher
                  </Box>
                </Button>
              </Tooltip>
            )}
            {/* Rejecting is the alternative to writing feedback, so it sits up here, decided first. */}
            {data.canReject && (
              <Tooltip title="Not ready for feedback? Send it back and ask the student to submit again.">
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<MdBlock />}
                  onClick={() => setIsRejectOpen(true)}
                  aria-label="Reject"
                  sx={ICON_ON_PHONE_SX}
                >
                  <Box component="span" sx={LABEL_FROM_MD_SX}>
                    Reject
                  </Box>
                </Button>
              </Tooltip>
            )}
          </Box>
        </Paper>
      </Box>

      {data.assignTeacherBlockedReason && (
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: '12px' }}>
          {data.assignTeacherBlockedReason} To fix it, give this user a student role for the program&apos;s app.
        </Alert>
      )}

      {isRejected && data.rejection && (
        <Alert severity="error" icon={<MdBlock size={22} />} sx={{ mb: 2.5, borderRadius: '12px' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Rejected {formatDate(data.rejection.rejectedAt)} — the student was asked to submit again
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
            {data.rejection.reason}
          </Typography>
        </Alert>
      )}

      <SubmissionNotes notes={data.notes} />

      <ReviewSplitView
        stickyTop={appHeaderHeight + stickyHeaderHeight}
        answersLabel={`Answers (${answerItems.length})`}
        feedbackLabel={feedbackTabLabel}
        answers={
          <Section title="Student’s answers">
            <AnswerList items={answerItems} numbered gradeBorderColor="#ececec" />
          </Section>
        }
        feedback={
          <Section
            title={data.canEdit || !data.reviewer ? 'Your feedback' : `Feedback from ${data.reviewer.name}`}
            aside={
              sentLine ? (
                <Typography variant="caption" color="text.secondary">
                  {sentLine}
                </Typography>
              ) : null
            }
          >
            {isRejected ? (
              <Typography variant="body2" color="text.secondary">
                No feedback for a rejected submission. The student&apos;s next submission arrives in your queue.
              </Typography>
            ) : data.feedbackForm ? (
              <FeedbackEditor
                key={data.submissionId}
                spaceId={subSpaceId}
                submissionId={data.submissionId}
                feedbackForm={data.feedbackForm}
                initialAnswers={data.feedback?.answers ?? []}
                isDelivered={isDelivered}
                hasDraft={!!data.feedback && !data.feedback.submittedAt}
                canEdit={data.canEdit}
              />
            ) : (
              <Alert severity="warning">
                No feedback form is linked to &quot;{data.form.title}&quot; yet. Ask an admin to set one on the form.
              </Alert>
            )}
          </Section>
        }
      />

      <RejectSubmissionDialog
        open={isRejectOpen}
        onClose={() => setIsRejectOpen(false)}
        spaceId={subSpaceId}
        submissionId={data.submissionId}
        studentName={data.student.name}
        hasDraft={!!data.feedback && !data.feedback.submittedAt}
      />
      <NotifySubmissionDialog
        open={isNotifyOpen}
        onClose={() => setIsNotifyOpen(false)}
        spaceId={subSpaceId}
        submissionId={data.submissionId}
        formTitle={data.form.title}
        studentName={data.student.name}
        teacherName={data.reviewer?.name ?? null}
      />
      <AssignTeacherDialog
        open={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
        spaceId={subSpaceId}
        submissionId={data.submissionId}
        studentName={data.student.name}
      />
    </Box>
  );
};
